import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/config/firebase';
import { Business } from '@/types/business';
import toast from 'react-hot-toast';
import {
  Trash2,
  Archive,
  Database,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive,
  Activity,
  Settings,
  Play,
  Calendar,
  Bell,
  Users,
  Building2,
  FileText
} from 'lucide-react';

interface DatabaseStats {
  businesses: number;
  users: number;
  classes: number;
  reservations: number;
  notifications: number;
}

interface CleanupResult {
  success: boolean;
  cleaned: {
    notifications: number;
    activityLogs: number;
  };
}

const SuperAdminMaintenancePage: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Estados de operaciones
  const [cleaningNotifications, setCleaningNotifications] = useState(false);
  const [archivingClasses, setArchivingClasses] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState<string | null>(null);

  // Configuración de retención
  const [retentionConfig, setRetentionConfig] = useState({
    notifications: 30,
    activityLogs: 90,
    archivedClasses: 180,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'businesses'));
        const items = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Business[];
        setBusinesses(items);
      await loadStats();
      } catch (e) {
      console.error('Error cargando datos:', e);
      toast.error('Error cargando datos');
      } finally {
        setLoading(false);
      }
    };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const getDatabaseStatsFn = httpsCallable(functions, 'getDatabaseStats');
      const result = await getDatabaseStatsFn();
      setStats(result.data as DatabaseStats);
    } catch (e: any) {
      console.error('Error cargando estadísticas:', e);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleGlobalCleanup = async () => {
    if (!confirm('¿Estás seguro? Esto eliminará notificaciones leídas antiguas y logs de actividad.')) {
      return;
    }

    setCleaningNotifications(true);
    try {
      const cleanupFn = httpsCallable(functions, 'cleanupTestData');
      const result = await cleanupFn();
      const data = result.data as CleanupResult;
      
      if (data.success) {
        toast.success(`Limpieza completada: ${data.cleaned.notifications} notificaciones, ${data.cleaned.activityLogs} logs eliminados`);
        await loadStats();
      } else {
        toast.error('Error en la limpieza');
      }
    } catch (e: any) {
      console.error('Error ejecutando limpieza:', e);
      toast.error(e?.message || 'Error ejecutando limpieza global');
    } finally {
      setCleaningNotifications(false);
    }
  };

  const handleArchiveClasses = async () => {
    if (!confirm('¿Archivar clases completadas de más de 6 meses? Las clases se moverán a una colección de archivo.')) {
      return;
    }

    setArchivingClasses(true);
    toast.loading('Archivando clases antiguas...', { id: 'archive' });
    
    try {
      // Esta función se ejecuta automáticamente cada semana,
      // pero podemos simular una ejecución manual aquí
      // Por ahora mostramos un mensaje informativo
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('El archivado se ejecuta automáticamente cada domingo a las 4:00 AM', { id: 'archive' });
    } catch (e: any) {
      toast.error('Error archivando clases', { id: 'archive' });
    } finally {
      setArchivingClasses(false);
    }
  };

  const handleCleanupBusiness = async (businessId: string, businessName: string) => {
    if (!confirm(`¿Limpiar datos ficticios de "${businessName}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    setRunningCleanup(businessId);
    try {
      const mod = await import('@/utils/cleanupFakeData');
      const result = await mod.cleanupFakeData(businessId);
      if ((result as any)?.success) {
        toast.success(`Limpieza de ${businessName} completada`);
      } else {
        toast.error('Error en limpieza');
      }
    } catch (e: any) {
      console.error('Error ejecutando limpieza:', e);
      toast.error(e?.message || 'Error ejecutando limpieza');
    } finally {
      setRunningCleanup(null);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('es-ES');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-amber-500" />
            Mantenimiento del Sistema
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Herramientas de limpieza y optimización de la base de datos
          </p>
        </div>
        <button 
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Advertencia */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800 dark:text-amber-200">
            Zona de operaciones sensibles
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
            Estas acciones afectan directamente a la base de datos. Úsalas con precaución y solo cuando sea necesario.
          </p>
        </div>
      </div>

      {/* Estadísticas de Base de Datos */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-500" />
            Estadísticas de Base de Datos
          </h2>
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? '...' : formatNumber(stats?.businesses || businesses.length)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Negocios</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <Users className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? '...' : formatNumber(stats?.users || 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Usuarios</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? '...' : formatNumber(stats?.classes || 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Clases</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <Bell className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? '...' : formatNumber(stats?.notifications || 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Notificaciones</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <HardDrive className="w-6 h-6 text-gray-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {statsLoading ? '...' : formatNumber(
                  (stats?.businesses || 0) + 
                  (stats?.users || 0) + 
                  (stats?.classes || 0) + 
                  (stats?.notifications || 0)
                )}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Docs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tareas de Limpieza Automática */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-500" />
            Tareas Programadas
          </h2>
        </div>
        <div className="p-4 space-y-4">
          {/* Limpieza diaria */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Trash2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Limpieza Diaria</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Elimina notificaciones leídas (+{retentionConfig.notifications} días) y logs (+{retentionConfig.activityLogs} días)
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Activa
              </span>
              <p className="text-xs text-gray-500 mt-1">Todos los días 3:00 AM</p>
            </div>
          </div>

          {/* Archivado semanal */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Archive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Archivado Semanal</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Archiva clases completadas de más de {retentionConfig.archivedClasses} días
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Activa
              </span>
              <p className="text-xs text-gray-500 mt-1">Domingos 4:00 AM</p>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones Manuales */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Play className="w-5 h-5 text-amber-500" />
            Acciones Manuales
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Limpieza Global */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Limpieza Global
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Ejecuta la limpieza de notificaciones y logs ahora mismo, sin esperar a la tarea programada.
                  </p>
                  <button
                    onClick={handleGlobalCleanup}
                    disabled={cleaningNotifications}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                  >
                    {cleaningNotifications ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Limpiando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Ejecutar Limpieza
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Archivar Clases */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Archive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Archivar Clases Antiguas
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Mueve clases completadas de más de 6 meses a la colección de archivo.
                  </p>
                  <button
                    onClick={handleArchiveClasses}
                    disabled={archivingClasses}
                    className="mt-3 flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                  >
                    {archivingClasses ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Archivando...
                      </>
                    ) : (
                      <>
                        <Archive className="w-4 h-4" />
                        Archivar Ahora
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuración de Retención */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Configuración de Retención
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notificaciones leídas
              </label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="number"
                  value={retentionConfig.notifications}
                  onChange={(e) => setRetentionConfig({
                    ...retentionConfig,
                    notifications: parseInt(e.target.value) || 30
                  })}
                  className="w-20 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-center"
                />
                <span className="text-sm text-gray-500">días</span>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Logs de actividad
              </label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="number"
                  value={retentionConfig.activityLogs}
                  onChange={(e) => setRetentionConfig({
                    ...retentionConfig,
                    activityLogs: parseInt(e.target.value) || 90
                  })}
                  className="w-20 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-center"
                />
                <span className="text-sm text-gray-500">días</span>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Clases archivadas
              </label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="number"
                  value={retentionConfig.archivedClasses}
                  onChange={(e) => setRetentionConfig({
                    ...retentionConfig,
                    archivedClasses: parseInt(e.target.value) || 180
                  })}
                  className="w-20 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-center"
                />
                <span className="text-sm text-gray-500">días</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
            ⚠️ Los cambios en la configuración requieren redesplegar las Cloud Functions para aplicarse.
          </p>
        </div>
      </div>

      {/* Limpieza por Negocio */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            Limpieza por Negocio
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Eliminar datos de prueba/ficticios de un negocio específico
          </p>
        </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Negocio
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Admin
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Estado
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Acciones
                </th>
                </tr>
              </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {businesses.map(b => (
                <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{b.name}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {b.adminEmail}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      b.subscription?.status === 'active'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {b.subscription?.status || 'activo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                      <button
                      onClick={() => handleCleanupBusiness(b.id, b.name)}
                      disabled={!!runningCleanup}
                      className="px-3 py-1.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {runningCleanup === b.id ? (
                        <span className="flex items-center gap-1">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          Limpiando...
                        </span>
                      ) : (
                        'Limpiar datos ficticios'
                      )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      {/* Estado de Cloud Functions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            Estado de Cloud Functions
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { name: 'dailyCleanup', desc: 'Limpieza diaria', schedule: '3:00 AM' },
              { name: 'weeklyArchive', desc: 'Archivado semanal', schedule: 'Dom 4:00 AM' },
              { name: 'stripeWebhook', desc: 'Webhooks de Stripe', schedule: 'Tiempo real' },
              { name: 'getDatabaseStats', desc: 'Estadísticas BD', schedule: 'Bajo demanda' },
            ].map((fn) => (
              <div key={fn.name} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{fn.name}</p>
                  <p className="text-xs text-gray-500">{fn.desc}</p>
                  <p className="text-xs text-gray-400">{fn.schedule}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminMaintenancePage;
